'use client';

import { useEffect, useState, type PropsWithChildren } from 'react';

import { useReducedMotion } from 'motion/react';
import Image from 'next/image';

import { AiMark, AiMarkRow } from '@/components/ai-mark';
import {
  HERO_DEMO_SURFACES,
  HERO_FUNNEL_COPY,
  HERO_FUNNEL_STAGES,
  HERO_FUNNEL_VERTICALS,
  type HeroDemoSurface,
  type HeroFunnelVertical,
} from '@/lib/content';
import { cn } from '@/lib/utils';

/** How long each stage holds before autoplay moves on. Also drives the progress bar. */
const STAGE_MS = 5200;
const STAGE_COUNT = HERO_FUNNEL_STAGES.length;
/** Gap between the entrance of one line of the demo and the next. */
const LINE_STAGGER_MS = 340;
/** Matches the `sm:` breakpoint the photo column uses. Below this the image is not shown. */
const PHOTO_MIN_WIDTH = '(min-width: 640px)';

function usePreloadImage(href: string | null) {
  useEffect(() => {
    if (!href) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    link.media = PHOTO_MIN_WIDTH;
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, [href]);
}

/**
 * Step-1 thread is a miniature of each product's own chat canvas, not our marketing theme.
 * Hexes follow the public ChatGPT and Claude.ai light UIs so the toggle change is readable.
 */
const CHAT_SKINS = {
  ChatGPT: {
    panel: 'bg-[#f7f7f8]',
    ask: 'max-w-[80%] self-end rounded-[22px] bg-[#ececec] px-3 py-2 text-sm leading-snug text-[#0d0d0d]',
    reply:
      'flex w-full flex-col gap-2.5 rounded-2xl border border-[#e5e5e5] bg-white p-2.5 text-[#0d0d0d]',
    price:
      'rounded-full border border-[#e5e5e5] bg-[#f7f7f8] px-2.5 py-0.5 font-mono text-xs whitespace-nowrap text-[#5d5d5d]',
    cta: 'rounded-full bg-[#0d0d0d] px-3.5 py-2 text-center text-sm font-medium text-white',
  },
  Claude: {
    panel: 'bg-[#f4f0e7]',
    ask: 'max-w-[80%] self-end rounded-xl bg-[#e7dfd2] px-3 py-2 text-sm leading-snug text-[#141413]',
    reply:
      'flex w-full flex-col gap-2.5 rounded-xl border border-[#e0d5c5] bg-[#fbf7f0] p-2.5 text-[#141413]',
    price:
      'rounded-md border border-[#e0d5c5] bg-[#f4f0e7] px-2.5 py-0.5 font-mono text-xs whitespace-nowrap text-[#6b6459]',
    cta: 'rounded-lg bg-[#d97757] px-3.5 py-2 text-center text-sm font-medium text-white',
  },
} as const;

type ChatSkin = (typeof CHAT_SKINS)[HeroDemoSurface];

/** One line of the mini conversation, rising in after the lines above it. */
function Line({
  index,
  className,
  children,
}: PropsWithChildren<{ index: number; className?: string }>) {
  return (
    <div
      style={{ animationDelay: `${index * LINE_STAGGER_MS}ms` }}
      className={cn('animate-rise-in motion-reduce:animate-none', className)}
    >
      {children}
    </div>
  );
}

/** What the shopper types. */
function Ask({ children }: PropsWithChildren) {
  return (
    <span className="max-w-[80%] self-end rounded-xl rounded-ee-[3px] bg-foreground px-3 py-2 text-sm leading-snug text-background">
      {children}
    </span>
  );
}

function Offer({ vertical }: { vertical: HeroFunnelVertical }) {
  return (
    <span className="flex items-center justify-between gap-2.5 rounded-[10px] border bg-background px-3 py-2.5 text-sm font-medium">
      <span className="min-w-0">{vertical.item}</span>
      <span className="rounded-full border bg-card px-2.5 py-0.5 font-mono text-xs whitespace-nowrap text-muted-foreground">
        {vertical.price}
      </span>
    </span>
  );
}

/** Catalog result the assistant returns in step 1: offer and CTA as one card. */
function AiReply({ vertical, skin }: { vertical: HeroFunnelVertical; skin: ChatSkin }) {
  return (
    <span className={skin.reply}>
      <span className="flex items-center justify-between gap-2.5 text-sm font-medium">
        <span className="min-w-0">{vertical.item}</span>
        <span className={skin.price}>{vertical.price}</span>
      </span>
      <span className={skin.cta}>{vertical.aiCta}</span>
    </span>
  );
}

/** The agent's call to action. A span, not a button - nothing here is clickable. */
function Cta({ children }: PropsWithChildren) {
  return (
    <span className="self-start rounded-lg bg-foreground px-3.5 py-2 text-sm font-medium text-background">
      {children}
    </span>
  );
}

function Settled({ children }: PropsWithChildren) {
  return (
    <span className="self-start rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm font-medium text-success">
      ✓ {children}
    </span>
  );
}

/** The quiet "who is doing this" caption. */
function Aside({ surface, children }: PropsWithChildren<{ surface?: HeroDemoSurface }>) {
  return (
    <span className="flex items-start gap-2 text-xs text-muted-foreground">
      {surface ? (
        <AiMark surface={surface} />
      ) : (
        <i aria-hidden="true" className="mt-0.5 size-3.5 flex-none rounded-[4px] bg-foreground" />
      )}
      <span className="min-w-0">{children}</span>
    </span>
  );
}

function Viz({ src, label }: { src: string; label: string }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className="relative hidden min-h-0 flex-[1_1_45%] overflow-hidden rounded-[10px] border bg-muted sm:block">
      {!failed && (
        <Image
          src={src}
          alt=""
          fill
          sizes="(min-width: 1280px) 16rem, (min-width: 640px) 40vw, 0px"
          className="object-cover"
          // Lazy + hidden below sm: the browser skips the fetch on phones. Desktop hits cache
          // from the preload issued when this slide was next, or loads now if it was a cold click.
          loading="lazy"
          unoptimized
          onError={() => setFailed(true)}
        />
      )}
      {/* Sits on the photo, so it stays light in both themes. */}
      <span className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)] truncate rounded-full bg-white/90 px-2.5 py-0.5 font-mono text-2xs text-[#111110]">
        {label}
      </span>
    </div>
  );
}

function StageDemo({
  vertical,
  stage,
  surface,
}: {
  vertical: HeroFunnelVertical;
  stage: number;
  surface: HeroDemoSurface;
}) {
  if (stage === 0) {
    const skin = CHAT_SKINS[surface];
    return (
      <div className={cn('flex flex-col gap-2.5 rounded-[10px] p-2.5', skin.panel)}>
        <Line index={0} className="flex flex-col">
          <span className={skin.ask}>{vertical.ask}</span>
        </Line>
        <Line index={1} className="flex flex-col">
          <AiReply vertical={vertical} skin={skin} />
        </Line>
      </div>
    );
  }

  if (stage === 1) {
    return (
      <>
        <Line index={0} className="flex flex-col">
          <Aside>
            {HERO_FUNNEL_COPY.handoff} {surface}
          </Aside>
        </Line>
        <Line index={1} className="flex flex-col">
          <Offer vertical={vertical} />
        </Line>
        <Line index={2} className="flex flex-wrap gap-2">
          {HERO_FUNNEL_COPY.payment.map(method => (
            <span
              key={method}
              className="rounded-full border bg-card px-2.5 py-1 font-mono text-xs whitespace-nowrap text-muted-foreground"
            >
              {method}
            </span>
          ))}
        </Line>
        <Line index={3} className="flex flex-col">
          <Cta>{vertical.siteCta}</Cta>
        </Line>
        <Line index={4} className="flex flex-col">
          <Settled>
            {vertical.confirmation}
            {HERO_FUNNEL_COPY.receipt}
          </Settled>
        </Line>
      </>
    );
  }

  return (
    <>
      <Line index={0} className="flex flex-col">
        <Ask>{vertical.serviceAsk}</Ask>
      </Line>
      <Line index={1} className="flex flex-col">
        <Settled>{vertical.serviceOk}</Settled>
      </Line>
    </>
  );
}

/** ChatGPT / Claude switcher for step 1. Stays outside the stage button so the controls do not nest. */
function SurfaceToggle({
  surface,
  onChange,
}: {
  surface: HeroDemoSurface;
  onChange: (next: HeroDemoSurface) => void;
}) {
  return (
    <span
      role="group"
      aria-label={HERO_FUNNEL_COPY.surfaceToggle}
      className="inline-flex flex-none overflow-hidden rounded-full border"
    >
      {HERO_DEMO_SURFACES.map((entry, i) => (
        <button
          key={entry}
          type="button"
          aria-pressed={entry === surface}
          onClick={() => onChange(entry)}
          className={cn(
            'inline-flex cursor-pointer items-center gap-1 px-2.5 py-1 text-xs font-medium transition-colors',
            i > 0 && 'border-s',
            entry === surface
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <AiMark surface={entry} />
          {entry}
        </button>
      ))}
    </span>
  );
}

function StageHeading({
  index,
  heading,
  active,
}: {
  index: number;
  heading: string;
  active: boolean;
}) {
  return (
    <span
      aria-current={active ? 'step' : undefined}
      className="flex min-w-0 flex-1 basis-full items-center gap-2.5 sm:basis-0"
    >
      <span
        className={cn(
          'inline-flex size-6.5 flex-none items-center justify-center rounded-full text-xs font-semibold',
          active ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground',
        )}
      >
        {index + 1}
      </span>
      <span className="text-sm leading-snug font-semibold">{heading}</span>
    </span>
  );
}

/** The mono label or assistant marks on the right of a stage header. */
function StageTag({
  vertical,
  stage,
  surface,
  onSurfaceChange,
}: {
  vertical: HeroFunnelVertical;
  stage: number;
  surface: HeroDemoSurface;
  onSurfaceChange: (next: HeroDemoSurface) => void;
}) {
  if (stage === 0) {
    return <SurfaceToggle surface={surface} onChange={onSurfaceChange} />;
  }

  return (
    <span className="flex items-center gap-1.5">
      <span className="font-mono text-2xs whitespace-nowrap text-faint-foreground">
        {vertical.domain} &
      </span>
      <AiMarkRow />
    </span>
  );
}

export function HeroFunnel({ className }: { className?: string }) {
  const [tick, setTick] = useState(0);
  // Autoplay stops for good on the first click: pulling someone off the industry they just chose
  // is both bad manners and a WCAG 2.2.2 failure, and it doubles as the required stop control.
  const [playing, setPlaying] = useState(true);
  const [surfaceOverride, setSurfaceOverride] = useState<HeroDemoSurface | null>(null);
  const [hoveredVertical, setHoveredVertical] = useState<number | null>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!playing || reducedMotion) return;
    const id = window.setInterval(() => setTick(current => current + 1), STAGE_MS);
    return () => window.clearInterval(id);
  }, [playing, reducedMotion]);

  const verticalIndex = Math.floor(tick / STAGE_COUNT) % HERO_FUNNEL_VERTICALS.length;
  const stageIndex = tick % STAGE_COUNT;
  const vertical = HERO_FUNNEL_VERTICALS[verticalIndex];
  const surface = surfaceOverride ?? vertical.ai;
  const nextVertical = HERO_FUNNEL_VERTICALS[(verticalIndex + 1) % HERO_FUNNEL_VERTICALS.length];
  const hoveredSrc = hoveredVertical !== null ? HERO_FUNNEL_VERTICALS[hoveredVertical].image : null;
  // Last stage is ~5s from the next vertical. Hover/focus covers a tab click that skips the timer.
  usePreloadImage(hoveredSrc ?? (stageIndex === STAGE_COUNT - 1 ? nextVertical.image : null));

  function show(nextTick: number) {
    setPlaying(false);
    const nextVertical = Math.floor(nextTick / STAGE_COUNT) % HERO_FUNNEL_VERTICALS.length;
    if (nextVertical !== verticalIndex) setSurfaceOverride(null);
    setTick(nextTick);
  }

  function chooseSurface(next: HeroDemoSurface) {
    setPlaying(false);
    setSurfaceOverride(next);
    setTick(verticalIndex * STAGE_COUNT);
  }

  return (
    <div
      className={cn('flex min-w-0 flex-col', className)}
      aria-label={HERO_FUNNEL_COPY.label}
      role="group"
    >
      <div className="mb-3.5 flex flex-wrap justify-center gap-2">
        {HERO_FUNNEL_VERTICALS.map((entry, index) => (
          <button
            key={entry.tab}
            type="button"
            aria-pressed={index === verticalIndex}
            onClick={() => show(index * STAGE_COUNT)}
            onMouseEnter={() => setHoveredVertical(index)}
            onMouseLeave={() => setHoveredVertical(null)}
            onFocus={() => setHoveredVertical(index)}
            onBlur={() => setHoveredVertical(null)}
            className={cn(
              'cursor-pointer rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
              index === verticalIndex
                ? 'border-foreground bg-foreground text-background'
                : 'bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {entry.tab}
          </button>
        ))}
      </div>

      {/* 540px is the photo+thread split. Below sm the photo is gone, so the thread has to
          size to its lines — a locked height clips the CTA. */}
      <div className="flex flex-col sm:h-[540px]">
        {HERO_FUNNEL_STAGES.map((stage, index) => {
          const active = index === stageIndex;
          const label = index === 2 ? vertical.serviceLabel : vertical.imageLabel;
          const caption =
            index === 0 ? `${HERO_FUNNEL_COPY.answeredInside} ${surface}` : stage.footer;
          return (
            <div key={stage.heading} className="contents">
              {index > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    background:
                      'repeating-linear-gradient(180deg, var(--border) 0 5px, transparent 5px 10px)',
                  }}
                  className="relative ms-[30px] h-6.5 w-0.5 flex-none"
                >
                  <i className="absolute -left-0.5 size-1.5 animate-connector-drop rounded-full bg-foreground motion-reduce:animate-none" />
                </span>
              )}
              {active ? (
                <div className="relative flex flex-col overflow-hidden rounded-xl border border-foreground bg-card shadow-lift transition-all sm:min-h-0 sm:flex-1">
                  {playing && !reducedMotion && (
                    <span
                      key={tick}
                      aria-hidden="true"
                      style={{ animationDuration: `${STAGE_MS}ms` }}
                      className="absolute inset-x-0 top-0 h-0.5 origin-left animate-stage-progress bg-foreground"
                    />
                  )}
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 p-3.5">
                    <StageHeading index={index} heading={stage.heading} active />
                    {index !== 1 && (
                      <span
                        className={cn(
                          index === 0
                            ? 'flex w-full justify-center sm:ms-auto sm:w-auto'
                            : 'ms-9 sm:ms-auto',
                        )}
                      >
                        <StageTag
                          vertical={vertical}
                          stage={index}
                          surface={surface}
                          onSurfaceChange={chooseSurface}
                        />
                      </span>
                    )}
                  </div>
                  <div
                    key={`${verticalIndex}-${stageIndex}-${surface}`}
                    className="flex gap-2.5 px-3.5 pb-3.5 sm:min-h-0 sm:flex-1"
                  >
                    <div className="flex min-w-0 flex-[1_1_55%] flex-col gap-2.5">
                      <StageDemo vertical={vertical} stage={index} surface={surface} />
                      {caption && (
                        <Line index={3} className="mt-auto flex flex-col">
                          {index === 2 ? (
                            <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              {caption}
                              <AiMarkRow />
                            </span>
                          ) : (
                            <Aside surface={index === 0 ? surface : undefined}>{caption}</Aside>
                          )}
                        </Line>
                      )}
                    </div>
                    <Viz key={vertical.image} src={vertical.image} label={label} />
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => show(verticalIndex * STAGE_COUNT + index)}
                  className="w-full flex-none cursor-pointer overflow-hidden rounded-xl border bg-card text-start opacity-55 transition-all hover:opacity-80"
                >
                  <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 p-3.5">
                    <StageHeading index={index} heading={stage.heading} active={false} />
                    {index === 2 && (
                      <span className="ms-9 max-sm:hidden sm:ms-auto">
                        <StageTag
                          vertical={vertical}
                          stage={index}
                          surface={surface}
                          onSurfaceChange={chooseSurface}
                        />
                      </span>
                    )}
                  </span>
                  <span className="block ps-12 pe-3.5 pb-3.5 text-xs text-muted-foreground">
                    {stage.idle}
                  </span>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
